import { slugify } from './slug.js';
import { fold } from './dutch.js';
import { parseSize } from './units.js';
/**
 * Pack sizes, counts and packaging — everything that says how much rather than
 * what. The same job `relevance.ts` does with its own `NOISE`, and deliberately
 * a separate list: that one clears the way for a *position* score, this one
 * decides *identity*, and the day one of them needs a word the other does not
 * is the day sharing it would be the bug.
 */
const SIZE_NOISE = /^(\d+([.,]\d+)?(l|ml|cl|dl|kg|g|gr|mg|pk|pack)?|l|ml|cl|dl|kg|g|gr|mg|x|st|stk|stuk|stuks|pk|pak|pack|fles|blik|zak|doos|bos|krat|net|bak|bakje|houdbaar)$/;
/**
 * Words that name a product *line* rather than the product, and that a shop is
 * free to leave out of its own title.
 *
 * Two words, not a vocabulary. Every word dropped here is a word two different
 * products are allowed to differ by without this noticing, so the list stays at
 * exactly what the live titles forced: Albert Heijn sells `Oatly! Haver barista
 * edition` and Jumbo sells the same litre as `Oatly! The Original Haver Barista
 * Edition`. Resist adding "bio", "vers", "nieuw" and their like — those are
 * what the product *is*, and the shops that print them mean them.
 */
const LINE_NOISE = new Set(['the', 'original']);
/**
 * Brand fields that are a shop saying it has no brand, in words.
 *
 * Ekoplaza files loose oranges under `Onbekend \ Merkloos` — "unknown /
 * brandless". Slugged, that is a perfectly good brand token, so the brand gate
 * — the thing that stops four shops' own-brand milk becoming one product — was
 * satisfied by a sentinel meaning the exact opposite (SEARCH_GAPS I5).
 *
 * Observed strings only. A general "brands that look like placeholders"
 * heuristic is a guess, and the failure it would cause is the one this file
 * exists to prevent. Adapters apply this too, so the empty field arrives empty
 * (ADAPTERS §2.1: an adapter may make a field *less* specific, never more).
 */
const PLACEHOLDER_BRANDS = new Set([
    'onbekend-merkloos',
    'merkloos',
    'onbekend',
    'geen-merk',
    'huismerk',
]);
/** True when a brand field is the shop admitting it has none. */
export function isPlaceholderBrand(brand) {
    const slug = slugify(brand ?? '');
    return slug.length === 0 || PLACEHOLDER_BRANDS.has(slug);
}
function tokensOf(text) {
    return slugify(text).split('-').filter(Boolean);
}
/**
 * A listing that is two products at one price: Lidl's `FANTA of SPRITE`,
 * `Galia- of cantaloupemeloen`, `Chili- of bonenschotel`. The shop is genuinely
 * selling one line at one price and letting the shopper take either.
 *
 * It has a brand, a size and title words, so it is bundle-eligible and its
 * price is a claim about whichever of two things you happen to pick up. There
 * is nothing to do with that but refuse it — and emphatically **not** split it
 * into two products, which would invent two prices the shop never printed.
 */
function isEitherOrListing(tokens) {
    const at = tokens.indexOf('of');
    return at > 0 && at < tokens.length - 1;
}
/**
 * A pack the *title* announces, for the shops that print it only there.
 *
 * Albert Heijn's size field for `Coca-Cola Original taste 6-pack` says
 * `6 stuks` — a count, not a pack — and Jumbo's `Coca-Cola Original Taste 4 x
 * 500 ml` reaches the client as a flat `2000 ml`, because totalling is what the
 * unit price needs. In both cases the title says plainly what the size field
 * lost, so it is read as a backstop.
 *
 * It only ever *adds* a multipack, never removes one, so every mistake it can
 * make is a refused bundle rather than a wrong one. "Vitamine B12 x 100" reads
 * as a pack of twelve and costs a comparison nobody was making.
 */
function packFromTitle(name) {
    const times = /(\d+)\s*[x×]\s*\d/.exec(name);
    if (times)
        return Number(times[1]);
    const pack = /(\d+)\s*[-\s]?pack\b/i.exec(name);
    if (pack)
        return Number(pack[1]);
    return null;
}
function formOf(size, declaredCount, name) {
    const count = declaredCount ?? size.packCount ?? packFromTitle(name);
    if (size.multipack || declaredCount || count !== null) {
        return { kind: 'multipack', count };
    }
    return { kind: 'single' };
}
function formKey(form) {
    return form.kind === 'single' ? 'single' : `multi:${form.count ?? '?'}`;
}
/**
 * What this row is, or why it cannot be said with enough confidence to put a
 * price comparison on it.
 */
export function productIdentity(item) {
    const brand = item.brand?.trim();
    if (!brand)
        return { ok: false, code: 'missing_brand', reason: 'the shop published no brand' };
    if (isPlaceholderBrand(brand)) {
        return {
            ok: false,
            code: 'sentinel_brand',
            reason: `"${brand}" is a placeholder, not a brand`,
        };
    }
    const brandSlug = slugify(brand);
    // Same route the size takes to the screen (`sizeLabel`), so a bundle cannot
    // group two rows the UI then prints different sizes under.
    const size = item.size_value === null || !item.size_unit
        ? null
        : parseSize(`${item.size_value} ${item.size_unit}`);
    if (!size) {
        return { ok: false, code: 'missing_size', reason: 'the pack size could not be read' };
    }
    const titleTokens = tokensOf(item.name);
    if (isEitherOrListing(titleTokens)) {
        return {
            ok: false,
            code: 'ambiguous_listing',
            reason: 'the listing is two products at one price',
        };
    }
    const brandTokens = new Set(tokensOf(brand).map(fold));
    const core = titleTokens
        .filter((token) => !SIZE_NOISE.test(token))
        .map(fold)
        .filter((token) => !brandTokens.has(token));
    // Sorted and de-duplicated: shops write the same qualifiers in different
    // orders — "Bio barista haver" and "barista haver bio" are one carton — and
    // word order is not something four marketing departments will ever agree on.
    const distinct = [...new Set(core.filter((token) => !LINE_NOISE.has(token)))].sort();
    const form = formOf(size, item.pack_count, item.name);
    return {
        ok: true,
        identity: {
            brand: brandSlug,
            form,
            size: { value: size.baseValue, unit: size.baseUnit },
            core,
            distinct,
            bare: core.length === 0,
            shelf: `${brandSlug}|${size.baseValue}|${size.baseUnit}|${formKey(form)}`,
            family: `${brandSlug}|${distinct.join('-')}`,
        },
    };
}
/** The identity, or null. For callers that do not want the refusal reason. */
export function identityOf(item) {
    const result = productIdentity(item);
    return result.ok ? result.identity : null;
}
/**
 * Words the household's own catalogue uses to *name a kind of product*, which
 * one shop prints in a title and another leaves out.
 *
 * Live, `doritos`: Albert Heijn's `Doritos Nacho cheese` and Jumbo's `Doritos
 * Nacho Cheese Tortilla Chips` are the same 170 g bag and were two rows. The
 * words that differ are the category, not a qualifier — and the difference
 * between those two is the whole problem, because letting one title be a
 * *subset* of another also merges `Oatly Haver` into `Oatly Haver Barista
 * Edition`, which is regular into barista, the exact failure ARCHITECTURE §4.4
 * names.
 *
 * So the extra words are allowed only when, taken together, they name
 * something the household already has a generic product for. That keeps it
 * derived from the household's own data rather than configured (constraint 6),
 * it is exact rather than fuzzy — the extras must match a whole name, not
 * overlap one — and it gets better as the catalogue grows, which is how the
 * rest of this app is meant to work.
 *
 * Build it once per render and hand it to `bundleByProduct`; with no vocabulary
 * the relaxation simply never fires.
 */
export function categoryVocabulary(names) {
    const vocabulary = new Set();
    for (const name of names) {
        const tokens = [...new Set(tokensOf(name).filter((t) => !SIZE_NOISE.test(t)).map(fold))].sort();
        // A one-word generic like "Melk" is a category word; a whole title is not.
        if (tokens.length > 0 && tokens.length <= 3)
            vocabulary.add(tokens.join('-'));
    }
    return vocabulary;
}
/**
 * Are these two rows the same purchasable product?
 *
 * The gates run in the order they are cheapest to disprove, and the first
 * failure is the answer. Every `match: true` carries the reasons it passed on,
 * so a rule that cannot explain itself cannot be added here.
 */
export function sameProduct(a, b, options = {}) {
    const left = productIdentity(a);
    if (!left.ok)
        return { match: false, code: left.code, reason: left.reason };
    const right = productIdentity(b);
    if (!right.ok)
        return { match: false, code: right.code, reason: right.reason };
    return matchIdentities(left.identity, right.identity, options);
}
/** The gate itself, for callers that already hold both identities. */
export function matchIdentities(a, b, options = {}) {
    if (a.brand !== b.brand) {
        return { match: false, code: 'brand_mismatch', reason: 'different brands' };
    }
    if (formKey(a.form) !== formKey(b.form)) {
        return { match: false, code: 'multipack_mismatch', reason: 'different package form' };
    }
    // An unknown count is not a matching count. A pack of we-don't-know-how-many
    // is exactly the case where a price comparison would be invented.
    if (a.form.kind === 'multipack' && a.form.count === null) {
        return { match: false, code: 'unknown', reason: 'pack count unknown' };
    }
    if (a.size.unit !== b.size.unit || a.size.value !== b.size.value) {
        return { match: false, code: 'size_mismatch', reason: 'different sizes' };
    }
    const shared = ['same brand', 'same package form', 'same size'];
    if (a.distinct.join('-') === b.distinct.join('-')) {
        return { match: true, confidence: 'certain', reasons: [...shared, 'same product words'] };
    }
    const extra = describedBy(a, b, options.categories);
    if (extra) {
        return {
            match: true,
            confidence: 'likely',
            reasons: [...shared, `one shop also names the category "${extra}"`],
        };
    }
    return { match: false, code: 'core_mismatch', reason: 'different product words' };
}
/**
 * Whether the two differ *only* by one shop naming the category, and the
 * household's own catalogue agrees that is what those words are.
 *
 * Deliberately not symmetric-fuzzy: the shorter side must be a complete subset
 * of the longer one, the leftover words must together name a known product, and
 * the shorter side must still say something of its own — otherwise this becomes
 * the bare-title rule without the guard that makes it safe.
 */
function describedBy(a, b, categories) {
    if (!categories || categories.size === 0)
        return null;
    const [shorter, longer] = a.distinct.length <= b.distinct.length ? [a, b] : [b, a];
    if (shorter.distinct.length === 0)
        return null;
    const longerWords = new Set(longer.distinct);
    if (!shorter.distinct.every((word) => longerWords.has(word)))
        return null;
    const extras = longer.distinct.filter((word) => !shorter.distinct.includes(word));
    if (extras.length === 0)
        return null;
    const key = [...extras].sort().join('-');
    return categories.has(key) ? key : null;
}
/**
 * The same product family in a different package — Coca-Cola Original as a 2 L
 * bottle and as 4 × 500 ml.
 *
 * Explicitly **not** `sameProduct`, and that is the point of having both. These
 * two cannot share a row and cannot be compared on price, because one of them
 * is not the other; but they are related, and saying so is more useful than
 * rendering them as strangers. Presentation is free to use it; identity is not.
 */
export function comparableProduct(a, b) {
    const left = productIdentity(a);
    if (!left.ok)
        return { match: false, code: left.code, reason: left.reason };
    const right = productIdentity(b);
    if (!right.ok)
        return { match: false, code: right.code, reason: right.reason };
    if (left.identity.family !== right.identity.family) {
        return { match: false, code: 'core_mismatch', reason: 'different products' };
    }
    if (matchIdentities(left.identity, right.identity).match) {
        // Not a refusal for want of evidence — the opposite. These two are one
        // product, so there is no *different package* to report.
        return { match: false, code: 'unknown', reason: 'the same product, not a different package' };
    }
    return { match: true, confidence: 'certain', reasons: ['same brand', 'same product words'] };
}
//# sourceMappingURL=identity.js.map